import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';

interface DispatchFooterContextType {
  dispatchButton: ReactNode | null;
  setDispatchButton: (button: ReactNode | null) => void;
  iconSizeSlider: ReactNode | null;
  setIconSizeSlider: (slider: ReactNode | null) => void;
  paginationControls: ReactNode | null;
  setPaginationControls: (controls: ReactNode | null) => void;
  todayStats: ReactNode | null;
  setTodayStats: (stats: ReactNode | null) => void;
  globalFilters: ReactNode | null;
  setGlobalFilters: (filters: ReactNode | null) => void;
  headerContent: ReactNode | null;
  setHeaderContent: (content: ReactNode | null) => void;
  refreshCountdown: number | null;
  setRefreshCountdown: (countdown: number | null) => void;
}

const DispatchFooterContext = createContext<DispatchFooterContextType | undefined>(undefined);

export const DispatchFooterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [dispatchButton, setDispatchButton] = useState<ReactNode | null>(null);
  const [iconSizeSlider, setIconSizeSlider] = useState<ReactNode | null>(null);
  const [paginationControls, setPaginationControls] = useState<ReactNode | null>(null);
  const [todayStats, setTodayStats] = useState<ReactNode | null>(null);
  const [globalFilters, setGlobalFilters] = useState<ReactNode | null>(null);
  const [headerContent, setHeaderContent] = useState<ReactNode | null>(null);
  const [refreshCountdown, setRefreshCountdown] = useState<number | null>(null);

  const value = useMemo(() => ({
    dispatchButton,
    setDispatchButton,
    iconSizeSlider,
    setIconSizeSlider,
    paginationControls,
    setPaginationControls,
    todayStats,
    setTodayStats,
    globalFilters,
    setGlobalFilters,
    headerContent,
    setHeaderContent,
    refreshCountdown,
    setRefreshCountdown
  }), [dispatchButton, iconSizeSlider, paginationControls, todayStats, globalFilters, headerContent, refreshCountdown]);

  return (
    <DispatchFooterContext.Provider value={value}>
      {children}
    </DispatchFooterContext.Provider>
  );
};

export const useDispatchFooter = () => {
  const context = useContext(DispatchFooterContext);
  if (!context) {
    throw new Error('useDispatchFooter must be used within a DispatchFooterProvider');
  }
  return context;
};
